import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string()
    .min(12, 'Password must be at least 12 characters')
    .max(128, 'Password must not exceed 128 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
    .refine((password) => {
      // Check for common patterns
      const commonPatterns = ['password', '123456', 'qwerty', 'abc123', 'admin', 'letmein', 'welcome', 'login', 'user', 'test']
      return !commonPatterns.some(pattern => password.toLowerCase().includes(pattern.toLowerCase()))
    }, 'Password contains common patterns that are not allowed')
    .refine((password) => {
      // Check for repeated characters (no 3+ consecutive identical chars)
      return !/(.)\1{2,}/.test(password)
    }, 'Password cannot contain 3 or more consecutive identical characters'),
  confirmPassword: z.string()
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type PasswordFormData = z.infer<typeof passwordSchema>

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  isTemporaryPassword?: boolean
}

export function PasswordChangeModal({ 
  isOpen, 
  onClose, 
  onSuccess, 
  isTemporaryPassword = false 
}: PasswordChangeModalProps) {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema)
  })

  const newPassword = watch('newPassword')

  const getPasswordStrength = (password: string) => {
    let score = 0
    const checks = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
      noCommonPatterns: !['password', '123456', 'qwerty', 'abc123', 'admin', 'letmein', 'welcome', 'login', 'user', 'test'].some(pattern => 
        password.toLowerCase().includes(pattern.toLowerCase())
      ),
      noRepeatedChars: !/(.)\1{2,}/.test(password)
    }

    Object.values(checks).forEach(check => {
      if (check) score++
    })

    return { score, checks }
  }

  const passwordStrength = getPasswordStrength(newPassword || '')

  const onSubmit = async (data: PasswordFormData) => {
    setIsSubmitting(true)
    setSubmitError('')

    try {
      // Simulate API call to change password
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // In a real app, this would call the password change API
      // await authService.changePassword(data.currentPassword, data.newPassword)
      
      onSuccess()
      reset()
    } catch (error) {
      setSubmitError('Failed to change password. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    reset()
    setSubmitError('')
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl font-semibold flex items-center justify-center space-x-2">
            <Lock className="h-5 w-5 text-primary-600" />
            <span>
              {isTemporaryPassword ? 'Change Temporary Password' : 'Change Password'}
            </span>
          </CardTitle>
          {isTemporaryPassword && (
            <div className="mt-2">
              <Badge className="bg-orange-100 text-orange-800">
                <AlertCircle className="h-3 w-3 mr-1" />
                Security Required
              </Badge>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          {isTemporaryPassword && (
            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-orange-800">Security Notice</h4>
                  <p className="text-sm text-orange-700 mt-1">
                    You're using a temporary password. For security reasons, you must change it before accessing the portal.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('currentPassword')}
                  type={showCurrentPassword ? 'text' : 'password'}
                  placeholder="Enter current password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.currentPassword.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('newPassword')}
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.newPassword.message}</p>
              )}
            </div>

            {/* Password Strength Indicator */}
            {newPassword && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">Password Strength:</div>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`h-2 flex-1 rounded ${
                        level <= passwordStrength.score
                          ? passwordStrength.score <= 2
                            ? 'bg-red-500'
                            : passwordStrength.score <= 3
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-xs text-gray-600">
                  {passwordStrength.score <= 3 && 'Weak'}
                  {passwordStrength.score === 4 && 'Medium'}
                  {passwordStrength.score === 5 && 'Strong'}
                  {passwordStrength.score === 6 && 'Very Strong'}
                  {passwordStrength.score === 7 && 'Excellent'}
                </div>

                {/* Password Requirements */}
                <div className="space-y-1 text-xs">
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.length ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>At least 12 characters</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.uppercase ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>One uppercase letter</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.lowercase ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>One lowercase letter</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.number ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>One number</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.special ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>One special character</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.noCommonPatterns ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>No common patterns</span>
                  </div>
                  <div className={`flex items-center space-x-2 ${
                    passwordStrength.checks.noRepeatedChars ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    <CheckCircle className="h-3 w-3" />
                    <span>No repeated characters</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  {...register('confirmPassword')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-600 text-sm mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-800">{submitError}</span>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              {!isTemporaryPassword && (
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              )}
              <Button 
                type="submit" 
                disabled={isSubmitting || passwordStrength.score < 4}
                className="bg-primary-600 hover:bg-primary-700"
              >
                {isSubmitting ? 'Changing Password...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
